/** Application settings, their persistence, and the tuning derived from them. */

import {
  A4_MIDI,
  RANKS,
  frequencyOf,
  rankById,
  temperatureRatio,
  type Tuning,
} from "./core/pitch.js";
import { TEMPERAMENTS, temperamentById } from "./core/temperaments.js";
import { WAVEFORMS, waveformById } from "./core/synth.js";

export interface Settings {
  /** Frequency of A4 in Hz. */
  referenceHz: number;
  temperamentId: string;
  rankId: string;
  waveformId: string;
  /** Output level, 0..1. */
  volume: number;
  /** Keep notes sounding after the key is released. */
  hold: boolean;
  /** Lowest octave shown on the keyboard. */
  keyboardOctave: number;
  temperatureEnabled: boolean;
  /** Air temperature in the building right now, in Celsius. */
  temperatureNowC: number;
  /** Temperature the instrument should be in tune at, in Celsius. */
  temperatureTargetC: number;
  beatRootMidi: number;
  beatIntervalId: string;
}

export const DEFAULT_SETTINGS: Settings = {
  referenceHz: 440,
  temperamentId: "equal",
  rankId: "8",
  waveformId: "principal",
  volume: 0.6,
  hold: false,
  keyboardOctave: 3,
  temperatureEnabled: false,
  temperatureNowC: 18,
  temperatureTargetC: 18,
  beatRootMidi: 60,
  beatIntervalId: "fifth",
};

/** Reference pitches you actually meet on instruments. */
export const PITCH_PRESETS: readonly { hz: number; label: string }[] = [
  { hz: 415, label: "415 baroque" },
  { hz: 430, label: "430 classical" },
  { hz: 435, label: "435 old French" },
  { hz: 440, label: "440 standard" },
  { hz: 442, label: "442" },
  { hz: 465, label: "465 chorton" },
];

export const KEYBOARD_OCTAVE_MIN = 0;
export const KEYBOARD_OCTAVE_MAX = 6;
export const OCTAVES_SHOWN = 3;

const STORAGE_KEY = "organ-tuner/settings/v1";

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

function oneOf<T extends { id: string }>(items: readonly T[], id: unknown, fallback: string): string {
  return typeof id === "string" && items.some((item) => item.id === id) ? id : fallback;
}

/**
 * Rebuild settings from an unknown value, field by field.
 *
 * Stored JSON is not to be trusted: it may come from an older version of the
 * app, or from a hand-edited localStorage. Anything unrecognised falls back to
 * its default rather than propagating a NaN into the frequency maths, where it
 * would surface as a silent oscillator and a blank display.
 */
export function sanitise(raw: unknown): Settings {
  const input = (typeof raw === "object" && raw !== null ? raw : {}) as Partial<
    Record<keyof Settings, unknown>
  >;
  const num = (value: unknown, fallback: number): number =>
    typeof value === "number" && Number.isFinite(value) ? value : fallback;

  return {
    referenceHz: clamp(num(input.referenceHz, DEFAULT_SETTINGS.referenceHz), 380, 500),
    temperamentId: oneOf(TEMPERAMENTS, input.temperamentId, DEFAULT_SETTINGS.temperamentId),
    rankId: oneOf(RANKS, input.rankId, DEFAULT_SETTINGS.rankId),
    waveformId: oneOf(WAVEFORMS, input.waveformId, DEFAULT_SETTINGS.waveformId),
    volume: clamp(num(input.volume, DEFAULT_SETTINGS.volume), 0, 1),
    hold: typeof input.hold === "boolean" ? input.hold : DEFAULT_SETTINGS.hold,
    keyboardOctave: Math.round(
      clamp(
        num(input.keyboardOctave, DEFAULT_SETTINGS.keyboardOctave),
        KEYBOARD_OCTAVE_MIN,
        KEYBOARD_OCTAVE_MAX,
      ),
    ),
    temperatureEnabled:
      typeof input.temperatureEnabled === "boolean"
        ? input.temperatureEnabled
        : DEFAULT_SETTINGS.temperatureEnabled,
    temperatureNowC: clamp(num(input.temperatureNowC, DEFAULT_SETTINGS.temperatureNowC), -10, 45),
    temperatureTargetC: clamp(
      num(input.temperatureTargetC, DEFAULT_SETTINGS.temperatureTargetC),
      -10,
      45,
    ),
    beatRootMidi: Math.round(clamp(num(input.beatRootMidi, DEFAULT_SETTINGS.beatRootMidi), 24, 96)),
    beatIntervalId:
      typeof input.beatIntervalId === "string"
        ? input.beatIntervalId
        : DEFAULT_SETTINGS.beatIntervalId,
  };
}

type Listener = (settings: Settings) => void;

export class SettingsStore {
  private settings: Settings;
  private readonly listeners = new Set<Listener>();

  constructor() {
    this.settings = sanitise(this.load());
  }

  private load(): unknown {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      return stored ? JSON.parse(stored) : {};
    } catch {
      // Private browsing, disabled storage, or corrupt JSON. Defaults are fine.
      return {};
    }
  }

  get current(): Settings {
    return this.settings;
  }

  update(patch: Partial<Settings>): void {
    const next = sanitise({ ...this.settings, ...patch });
    this.settings = next;
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
    } catch {
      // Not being able to remember the settings is not a reason to stop working.
    }
    for (const listener of this.listeners) listener(next);
  }

  subscribe(listener: Listener): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }
}

export function tuningOf(settings: Settings): Tuning {
  return {
    referenceHz: settings.referenceHz,
    anchorMidi: A4_MIDI,
    temperament: temperamentById(settings.temperamentId),
  };
}

/** Semitones the selected rank sounds above the key that plays it. */
export function rankOffset(settings: Settings): number {
  return rankById(settings.rankId).semitones;
}

/** The note a key actually sounds on the selected rank. */
export function soundingMidi(keyMidi: number, settings: Settings): number {
  return keyMidi + rankOffset(settings);
}

/**
 * Frequency multiplier that puts a pipe in tune at the target temperature
 * rather than at the temperature of the room you are standing in.
 *
 * A flue pipe tuned here and now to frequency f will sound
 * `f * ratio(now -> target)` once the building reaches the target. To land on
 * the wanted frequency then, tune to the wanted frequency divided by that —
 * which is the ratio taken the other way round. A church that will be warmer
 * during the service than it is at eight in the morning therefore wants its
 * pipes left slightly flat.
 */
export function temperatureAdjustment(settings: Settings): number {
  if (!settings.temperatureEnabled) return 1;
  return temperatureRatio(settings.temperatureTargetC, settings.temperatureNowC);
}

/** Target frequency of a sounding note, including any temperature compensation. */
export function targetFrequency(midi: number, settings: Settings): number {
  return frequencyOf(midi, tuningOf(settings)) * temperatureAdjustment(settings);
}

/** Cents the temperature compensation is shifting every note by. */
export function temperatureCentsShift(settings: Settings): number {
  return 1200 * Math.log2(temperatureAdjustment(settings));
}

export function waveformOf(settings: Settings) {
  return waveformById(settings.waveformId);
}

export function temperamentOf(settings: Settings) {
  return temperamentById(settings.temperamentId);
}

export function rankOf(settings: Settings) {
  return rankById(settings.rankId);
}
